/**
 * The Package panel's decisions, headless (the ADR-015 §9 idiom — a value
 * module beside a DOM shell, so the rules are testable without a browser).
 *
 * The panel renders WHAT THE FILE WOULD SAY and what World would do with it
 * (ADR-033, design note §4): every row is a measurement or a choice, and the
 * readiness list is the composer's own refusal list — the same words the
 * Package button is disabled by. Nothing here fetches, writes the Document
 * or touches the scene: it takes the Document, the session's bone names and
 * the library read, and returns a view.
 *
 * What lives where:
 *  - CHOICES are `document.package` (lenient, F6); absent = the defaults
 *    below (avatar, human/neutral, 1.9 m — the height World scales every
 *    avatar to today). Every edit commits a whole choices block through
 *    `package.set`; the helpers below return the next block.
 *  - IDENTITY: the id is the author's when set, else the roster row with the
 *    SAME NAME (Allen's replacement convention — the classic ids are
 *    `<name>-tripo-handed`), else `slugify(name)` for a new row.
 *  - PICKER FACETS: World's avatar-picker filters, author-set per facet; a
 *    facet left unset shows (and ships) the REPLACED row's value — the same
 *    `resolvePickerFacets` the composer runs, so what the panel shows is what
 *    the record carries. A new family is a warning beside the reasons.
 *  - READINESS: `composePackageRecord` run against the session names with a
 *    placeholder provenance; its `PackageRefusal.reasons` IS the list. A
 *    thumbnail is not asked of the composer, so the missing card is added
 *    here the way the door adds it (packageAvatar refuses a null thumbnail).
 *  - THE POOL: the rig's badged library (bake over prod), the selection-loop
 *    candidates = every LOOPABLE clip (calm groups first), the preference rows from
 *    what World inherits at this avatar's entity path.
 *
 * Depends on: kernel (package, library, document types), app package
 * (library-read). Depended on by: package-panel, main, tests.
 */
import {
  HEIGHT_METERS_BAND,
  PICKER_PRESENTATIONS,
  PackageRefusal,
  composePackageRecord,
  entityPathFor,
  prefillPackageId,
  resolvePickerFacets,
  rigFactsFromBoneNames,
  slugify,
  type AnimatorDocument,
  type ClipBadge,
  type InheritedBinding,
  type PackageChoices,
  type PackageFacets,
  type PackagePin,
  type PackageRole,
  type PickerFacetKey,
  type PickerFacets,
  type ResolvedPickerFacets,
  type RigFacts,
  type RosterRow,
} from '@harryhausen/kernel';
import { libraryForRig, type LibraryForRig, type LibraryRead } from '../package/library-read.js';

/** The height World scales every avatar to today; the default is a declaration, not an absence. */
export const DEFAULT_HEIGHT_METERS = 1.9;
/** The shelf this packager ships to. */
export const PACKAGE_SET = 'enhanced-2026';
/** Groups a selection loop may come from — the carousel shows a character standing, not fighting. */
/**
 * Groups a selection loop is offered from FIRST — the calm ones an author
 * usually wants. It is an ORDERING, not a gate: World's carousel plays
 * whatever clipId the record names, so a locomotion or combat clip loops
 * there exactly as well (Allen's 2026-09-06 pool is deliberately half
 * `backflip`/`punching-bag`/`cross-jumps`, and gating on the group refused 12
 * of 70 bodies in the first batch for a rule the consumer does not have).
 * The one real requirement is `loopable`.
 */
export const SELECTION_LOOP_GROUPS: readonly string[] = ['idle', 'social'];

export function defaultChoices(): PackageChoices {
  return {
    roles: ['avatar'],
    facets: { body: 'human', presentation: 'neutral' },
    heightMeters: { value: DEFAULT_HEIGHT_METERS, provenance: 'default' },
    pins: [],
    heroTakes: [],
  };
}

export interface PackagePanelInputs {
  readonly doc: AnimatorDocument;
  /** The session rig's bone names (grafts still suffixed). */
  readonly boneNames: readonly string[];
  /** null = not read yet (the panel asks for it on first render). */
  readonly library: LibraryRead | null;
  /**
   * Take names whose PNG bytes THIS session holds (phase 6). The Document
   * remembers takes by filename (F6); a reopened Document names takes whose
   * bytes are gone, and the starred one is then a reason until retaken.
   */
  readonly takesInSession?: readonly string[];
}

export type IdSource = 'chosen' | 'roster' | 'derived' | 'none';

export interface IdentityView {
  readonly name: string;
  readonly id: string | null;
  readonly idSource: IdSource;
  /** The roster row this package would REPLACE, when the id names one. */
  readonly replaces: RosterRow | null;
  /** A roster defect (two rows, one name) — shown, never guessed past. */
  readonly rosterProblem: string | null;
  readonly artist: string;
  readonly tags: readonly string[];
  readonly facets: PackageFacets;
  /** The picker facets as the record will carry them — authored, or carried from `replaces`. */
  readonly picker: ResolvedPickerFacets;
  readonly heightMeters: { readonly value: number; readonly provenance: 'default' | 'declared' };
  readonly entityPath: string | null;
}

export interface ReadinessRow {
  readonly key: 'family' | 'rig' | 'declarations' | 'height' | 'identity' | 'picker' | 'roles' | 'loop' | 'hero' | 'library';
  readonly label: string;
  readonly ok: boolean;
  readonly detail: string;
}

export interface SelectionLoopView {
  readonly chosen: string | undefined;
  readonly candidates: readonly ClipBadge[];
  /** Why there are no candidates, when there are none. */
  readonly reason: string | null;
}

/**
 * What a row's pin select OFFERS, paired to the row (Allen, 2026-09-05: "pair
 * walks with walk assignments, idle with idle assignments, to ease
 * assignment"). The row's KIND is read from what World inherits for it — the
 * pool's group and action of the inherited clips (a pin's clips for an
 * orphan row) — never from a table of key names, so a new key or a re-grouped
 * bake needs nothing here. `matched` = same group AND action; `sameGroup` =
 * the group's other actions; `rest` = everything else, still offered.
 */
export interface PinCandidates {
  readonly kind: { readonly group: string; readonly action: string | null } | null;
  readonly matched: readonly ClipBadge[];
  readonly sameGroup: readonly ClipBadge[];
  readonly rest: readonly ClipBadge[];
}

/** The most common (group, action) among the clips the pool carries; null when it carries none of them. */
export function kindOfClips(clipIds: readonly string[], pool: readonly ClipBadge[]): PinCandidates['kind'] {
  const counts = new Map<string, { group: string; action: string | null; n: number }>();
  for (const id of clipIds) {
    const badge = pool.find((c) => c.clipId === id);
    if (badge === undefined) continue;
    const action = badge.record.action ?? null;
    const k = `${badge.group}/${action ?? ''}`;
    const hit = counts.get(k);
    if (hit) hit.n += 1;
    else counts.set(k, { group: badge.group, action, n: 1 });
  }
  let best: { group: string; action: string | null; n: number } | null = null;
  for (const c of counts.values()) if (best === null || c.n > best.n) best = c;
  return best === null ? null : { group: best.group, action: best.action };
}

export function pinCandidatesFor(kind: PinCandidates['kind'], pool: readonly ClipBadge[]): PinCandidates {
  if (kind === null) return { kind, matched: [], sameGroup: [], rest: [...pool] };
  const matched: ClipBadge[] = [];
  const sameGroup: ClipBadge[] = [];
  const rest: ClipBadge[] = [];
  for (const c of pool) {
    if (c.group !== kind.group) rest.push(c);
    else if (kind.action === null || (c.record.action ?? null) === kind.action) matched.push(c);
    else sameGroup.push(c);
  }
  return { kind, matched, sameGroup, rest };
}

export interface PreferenceRow {
  readonly key: string;
  readonly state: string;
  /**
   * What World resolves today at this avatar's path, before any pin; null
   * for a saved pin whose (key, state) World no longer inherits here (the
   * sets or contract keys moved on) — the row stays, so the pin can be
   * inspected and lifted, never shipped unseen (Codex P2, #152 round three).
   */
  readonly inherited: InheritedBinding | null;
  /** The author's pin for this (key, state), if any. */
  readonly pin: PackagePin | undefined;
  /** What plays if you press ▶: the pin's first clip, else the inherited pool's first. */
  readonly sample: string | null;
  /** What the pin select offers for THIS row, paired to its kind. */
  readonly candidates: PinCandidates;
  /**
   * A combat preference — shown only with the npc role (design note §4:
   * "combat keys appear with the NPC role"), unless it carries a pin, which
   * stays visible so the author can lift it (a hidden pin cannot be undone).
   */
  readonly npcOnly: boolean;
}

/**
 * A combat preference: the binding's state is `combat`, or every clip of it
 * the pool carries is in the `combat` group (World's contractKeys name
 * combat clips under neutral-state keys — `meleeAttack`, `attack_0`).
 * Unknown clips decide nothing.
 */
export function isCombatPreference(state: string | undefined, clips: readonly string[], groupOf: (clipId: string) => string | undefined): boolean {
  if (state === 'combat') return true;
  const groups = clips.map(groupOf).filter((g): g is string => g !== undefined);
  return groups.length > 0 && groups.every((g) => g === 'combat');
}

export interface LibraryView {
  readonly status: 'unread' | 'read';
  readonly pool: string | null;
  readonly prod: string | null;
  readonly shipped: number;
  readonly staged: number;
  readonly bakeName: string | null;
  readonly errors: readonly string[];
  readonly reasons: readonly string[];
}

export interface PackagePanelView {
  readonly choices: PackageChoices;
  readonly facts: RigFacts;
  readonly declarations: readonly string[];
  readonly rig: LibraryForRig | null;
  readonly identity: IdentityView;
  readonly readiness: readonly ReadinessRow[];
  /** The composer's own list — the Package button's disabled reason, verbatim. */
  readonly reasons: readonly string[];
  /** Said beside the reasons, never blocking (a new picker family). */
  readonly warnings: readonly string[];
  readonly ready: boolean;
  readonly selectionLoop: SelectionLoopView;
  readonly preferences: readonly PreferenceRow[];
  readonly library: LibraryView;
}

const PLACEHOLDER_PROVENANCE = { harry: 'panel-preview', glbSha256: '0'.repeat(64), packagedAt: '2026-01-01T00:00:00.000Z' };

function declarationsOf(doc: AnimatorDocument): string[] {
  const out: string[] = [];
  if (doc.edits.headSpace !== undefined) out.push('headSeating');
  if (doc.edits.armSpace !== undefined) out.push('armSpace');
  return out;
}

export function identityOf(doc: AnimatorDocument, choices: PackageChoices, library: LibraryRead | null): IdentityView {
  const name = choices.name ?? doc.name;
  let id: string | null = null;
  let idSource: IdSource = 'none';
  let replaces: RosterRow | null = null;
  let rosterProblem: string | null = null;
  if (library?.roster != null) {
    try {
      const prefill = prefillPackageId(library.roster, { ...(choices.id !== undefined ? { id: choices.id } : {}), name });
      if (prefill !== null) {
        id = prefill.id;
        idSource = prefill.source;
        replaces = prefill.replaces;
      }
    } catch (e) {
      rosterProblem = e instanceof Error ? e.message : String(e);
    }
  } else if (choices.id !== undefined) {
    id = choices.id;
    idSource = 'chosen';
  }
  if (id === null) {
    try {
      id = slugify(name);
      idSource = 'derived';
    } catch {
      id = null;
    }
  }
  const facets = choices.facets;
  return {
    name,
    id,
    idSource,
    replaces,
    rosterProblem,
    artist: choices.artist ?? '',
    tags: choices.tags ?? [],
    facets,
    picker: resolvePickerFacets(choices, replaces?.tags ?? null),
    heightMeters: choices.heightMeters,
    entityPath: id === null ? null : entityPathFor(facets, id),
  };
}

/** The composer's refusal list, or [] when it composes. `replaces` = the roster row the package replaces (its picker facets carry). */
export function readinessReasons(doc: AnimatorDocument, choices: PackageChoices, familyId: string | null, boneNames: readonly string[], replaces: { readonly tags: readonly string[] } | null = null): string[] {
  try {
    composePackageRecord({ doc, choices, family: familyId, boneNames, set: PACKAGE_SET, replaces, provenance: PLACEHOLDER_PROVENANCE });
    return [];
  } catch (e) {
    if (e instanceof PackageRefusal) return [...e.reasons];
    return [e instanceof Error ? e.message : String(e)];
  }
}

export function packagePanelView(inputs: PackagePanelInputs): PackagePanelView {
  const { doc, boneNames, library } = inputs;
  const choices = doc.package ?? defaultChoices();
  const facts = rigFactsFromBoneNames(boneNames);
  const declarations = declarationsOf(doc);
  const identity = identityOf(doc, choices, library);
  const rig = library === null || identity.entityPath === null ? null : libraryForRig(library, boneNames, identity.entityPath);
  const familyId = rig?.detection.family?.id ?? null;
  const reasons = readinessReasons(doc, { ...choices, ...(identity.id !== null ? { id: identity.id } : {}) }, familyId, boneNames, identity.replaces);
  // The door refuses a null thumbnail too (packageAvatar); the panel says it in the same list.
  const inSession = new Set(inputs.takesInSession ?? []);
  const favouriteMissing = choices.heroFavourite !== undefined && !inSession.has(choices.heroFavourite);
  // Other takes without bytes are SAID, not refused: the card is what ships; the
  // folder door writes the takes it has and names the ones it lacks, and World's
  // ingest ignores extra takes either way (Codex P2, #154 round five).
  const goneTakes = choices.heroTakes.filter((t) => t !== choices.heroFavourite && !inSession.has(t));
  if (favouriteMissing) reasons.push(`the starred take "${choices.heroFavourite}" has no PNG in this session — retake it and star the new one`);

  const pool = rig?.badged ?? null;
  // The composer accepts any slug-legal clipId; the POOL is what World can
  // resolve. A reopened Document's loop or pin that the active pool no longer
  // carries (a re-bake, another folder) is a reason, never a silent 404
  // shipped as a success (Codex P2, #152).
  const poolIds = pool === null ? null : new Set(pool.clips.map((c) => c.clipId));
  // Every LOOPABLE clip is a candidate; the calm groups sort first so the list
  // opens where an author usually looks (see SELECTION_LOOP_GROUPS).
  const loopCandidates =
    pool === null
      ? []
      : pool.clips
          .filter((c) => c.record.loopable === true)
          .slice()
          .sort((a, b) => {
            const rank = (g: string): number => (SELECTION_LOOP_GROUPS.includes(g) ? 0 : 1);
            return rank(a.group) - rank(b.group);
          });
  // The saved loop must still be a CANDIDATE — in the pool AND loopable idle/social.
  // A re-bake can keep the id and lose the loop (Codex P2, #152 round four).
  const loopProblem: string | null =
    pool === null || poolIds === null || choices.selectionClip === undefined
      ? null
      : !poolIds.has(choices.selectionClip)
        ? `not in the ${pool.familyId} ${pool.bakeVersion} pool`
        : !loopCandidates.some((c) => c.clipId === choices.selectionClip)
          ? 'in the pool but not loopable — the carousel would play it once and stop'
          : null;
  if (pool !== null && poolIds !== null) {
    const poolName = `${pool.familyId} ${pool.bakeVersion}`;
    if (loopProblem !== null) reasons.push(`selection loop "${choices.selectionClip}" is ${loopProblem}`);
    for (const pin of choices.pins) {
      for (const c of pin.clips) {
        if (!poolIds.has(c)) reasons.push(`pin ${pin.key}${pin.state !== undefined ? ` (${pin.state})` : ''}: "${c}" is not in the ${poolName} pool`);
      }
    }
  }
  const selectionLoop: SelectionLoopView = {
    chosen: choices.selectionClip,
    candidates: loopCandidates,
    reason:
      pool === null
        ? library === null
          ? 'library not read yet'
          : (rig?.reasons[0] ?? 'no pool for this rig')
        : loopCandidates.length === 0
          ? `no loopable clip in ${pool.familyId} ${pool.bakeVersion}`
          : null,
  };

  // Preference rows: what World inherits here, the combat ones only with the
  // npc role (Codex P2, #152 round two) — an avatar-only package neither
  // shows nor ships NPC-oriented preferences. A combat row that carries a
  // pin stays visible, badged, and the pin is a reason until the role is
  // ticked or the pin lifted: said, never silently dropped.
  const groupOf = (clipId: string): string | undefined => pool?.clips.find((c) => c.clipId === clipId)?.group;
  const npc = choices.roles.includes('npc');
  const preferences: PreferenceRow[] = [];
  for (const inherited of rig?.inherited ?? []) {
    const pin = choices.pins.find((p) => p.key === inherited.key && (p.state ?? 'neutral') === inherited.state);
    // The ROW is combat by what World inherits for it — never by what the author
    // pinned onto it; a walk pinned onto meleeAttack is still a combat key (Codex P2, #152 round four).
    const npcOnly = isCombatPreference(inherited.state, inherited.binding.clips, groupOf);
    if (npcOnly && !npc && pin === undefined) continue;
    const candidates = pinCandidatesFor(kindOfClips(inherited.binding.clips, pool?.clips ?? []), pool?.clips ?? []);
    preferences.push({ key: inherited.key, state: inherited.state, inherited, pin, sample: pin?.clips[0] ?? inherited.binding.clips[0] ?? null, candidates, npcOnly });
  }
  // A saved pin with no inherited row (a reopened Document, a library that moved on) is still a row.
  if (rig !== null) {
    for (const pin of choices.pins) {
      const state = pin.state ?? 'neutral';
      if (preferences.some((p) => p.key === pin.key && p.state === state)) continue;
      const npcOnly = isCombatPreference(pin.state, pin.clips, groupOf);
      const candidates = pinCandidatesFor(kindOfClips(pin.clips, pool?.clips ?? []), pool?.clips ?? []);
      preferences.push({ key: pin.key, state, inherited: null, pin, sample: pin.clips[0] ?? null, candidates, npcOnly });
    }
  }
  if (!npc) {
    for (const row of preferences) {
      if (row.npcOnly && row.pin !== undefined) {
        reasons.push(`pin ${row.key}${row.state !== 'neutral' ? ` (${row.state})` : ''} is a combat preference — it ships only with the npc role`);
      }
    }
  }

  const readiness: ReadinessRow[] = [
    {
      key: 'library',
      label: 'library',
      ok: library !== null && (library.prod.some((p) => p.manifest !== null) || library.bake !== null),
      detail:
        library === null
          ? 'not read yet'
          : pool !== null
            ? `${pool.familyId} ${pool.bakeVersion}${pool.prodVersion !== null ? ` · prod ${pool.prodVersion}` : ` · prod: ${pool.prodUnknown ?? 'unknown'}`} · ${pool.shipped} shipped, ${pool.staged} staged`
            : (library.errors[0] ?? 'no family manifest read'),
    },
    {
      key: 'family',
      label: 'family',
      ok: familyId !== null,
      detail: familyId !== null ? `${familyId} (${rig!.detection.family!.matched} signature bones matched)` : (rig?.reasons[0] ?? 'no library read'),
    },
    {
      key: 'rig',
      label: 'rig',
      ok: true,
      detail: `${facts.bones} bones · digits ${facts.digits.left}/${facts.digits.right} · tail ${facts.tail}`,
    },
    {
      key: 'declarations',
      label: 'declarations',
      ok: true,
      detail: declarations.length > 0 ? declarations.join(' · ') : 'none (seating and arm-space at the file’s rest)',
    },
    {
      key: 'height',
      label: 'height',
      ok: choices.heightMeters.value > HEIGHT_METERS_BAND.min && choices.heightMeters.value <= HEIGHT_METERS_BAND.max,
      detail: `${choices.heightMeters.value.toFixed(2)} m · ${choices.heightMeters.provenance}`,
    },
    {
      key: 'identity',
      label: 'identity',
      ok: identity.id !== null && choices.artist !== undefined && identity.rosterProblem === null,
      detail:
        identity.rosterProblem ??
        `${identity.name} · id ${identity.id ?? '(none)'} (${identity.idSource}${identity.replaces !== null ? `, replaces ${identity.replaces.name}` : ''}) · artist ${choices.artist ?? '(none)'}`,
    },
    {
      key: 'picker',
      label: 'avatar picker',
      ok: identity.picker.refusals.length === 0,
      detail:
        identity.picker.tags.some((t) => /^(presentation|kind|style|family):/.test(t))
          ? `${identity.picker.tags.filter((t) => /^(presentation|kind|style|family):/.test(t)).join(' · ')}${identity.picker.carried.length > 0 ? ` (kept from the roster: ${identity.picker.carried.join(', ')})` : ''}`
          : 'no facets — shown under "Any" only, outside every filter',
    },
    {
      key: 'roles',
      label: 'use in World',
      ok: choices.roles.length > 0 && (!choices.roles.includes('npc') || choices.npc !== undefined),
      detail: `${choices.roles.join(' + ')}${choices.npc !== undefined ? ` · ${choices.npc.archetype}` : choices.roles.includes('npc') ? ' · npc archetype missing' : ''}`,
    },
    {
      key: 'loop',
      label: 'selection loop',
      ok: choices.selectionClip !== undefined && loopProblem === null,
      detail: choices.selectionClip === undefined ? (selectionLoop.reason ?? 'none chosen') : loopProblem !== null ? `${choices.selectionClip} — ${loopProblem}` : choices.selectionClip,
    },
    {
      key: 'hero',
      label: 'hero card',
      ok: choices.heroFavourite !== undefined && !favouriteMissing,
      detail:
        choices.heroTakes.length === 0
          ? 'no take yet — 📷 Take captures the viewport at this frame'
          : `${choices.heroTakes.length} take(s)${choices.heroFavourite !== undefined ? `, ★ ${choices.heroFavourite}${favouriteMissing ? ' (no PNG in this session)' : ''}` : ', none starred'}${goneTakes.length > 0 ? ` · ${goneTakes.length} other(s) without a PNG in this session — kept by name, not shipped` : ''}`,
    },
  ];

  return {
    choices,
    facts,
    declarations,
    rig,
    identity,
    readiness,
    reasons,
    warnings: [...identity.picker.warnings],
    ready: reasons.length === 0,
    selectionLoop,
    preferences,
    library: {
      status: library === null ? 'unread' : 'read',
      pool: pool === null ? null : `${pool.familyId} ${pool.bakeVersion}`,
      prod: pool?.prodVersion ?? null,
      shipped: pool?.shipped ?? 0,
      staged: pool?.staged ?? 0,
      bakeName: library?.bake?.name ?? null,
      errors: library?.errors ?? [],
      reasons: rig?.reasons ?? [],
    },
  };
}

// ───────────────────────── the next choices block, per gesture ─────────────────────────

/**
 * The id the roster supplied is WRITTEN into the choices on the first commit
 * (Codex P1, #152): shown only as a placeholder, every other edit would
 * commit a block without an id, and a later compose without the roster (a
 * reopened Document offline, the door's own composer) would derive
 * `slugify(name)` — `amari` — and append a new row beside `amari-tripo-handed`.
 * A chosen id is never overwritten; a derived one is left derived (the
 * composer derives the same).
 */
export function stabilizeId(view: PackagePanelView, next: PackageChoices): PackageChoices {
  if (next.id !== undefined) return next;
  if (view.identity.idSource === 'roster' && view.identity.id !== null) return { ...next, id: view.identity.id };
  return next;
}

export function withIdentity(choices: PackageChoices, patch: { name?: string; id?: string; artist?: string; tags?: readonly string[] }): PackageChoices {
  const next = { ...choices };
  if (patch.name !== undefined) {
    if (patch.name.trim() === '') delete next.name;
    else next.name = patch.name.trim();
  }
  if (patch.id !== undefined) {
    if (patch.id.trim() === '') delete next.id;
    else next.id = patch.id.trim();
  }
  if (patch.artist !== undefined) {
    if (patch.artist.trim() === '') delete next.artist;
    else next.artist = patch.artist.trim();
  }
  if (patch.tags !== undefined) {
    const tags = patch.tags.map((t) => t.trim()).filter((t) => t !== '');
    if (tags.length === 0) delete next.tags;
    else next.tags = tags;
  }
  return next;
}

/** A typed facet value as the tag-shaped slug the schema holds ("Club Kids" → `club-kids`). */
const facetValue = (v: string): string => v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

/**
 * The presentation list after one box is toggled. Built only from the values a
 * box shows: a carried out-of-vocabulary value (presentation:neutral) has no box
 * to uncheck, so an edit drops it rather than authoring it into a refusal (Codex P2, #231).
 */
export function togglePresentation(shown: readonly string[] | undefined, value: string, on: boolean): string[] {
  const now = (shown ?? []).filter((y) => PICKER_PRESENTATIONS.includes(y) && y !== value);
  return on ? [...now, value] : now;
}

/**
 * Set or clear picker facets. A facet set to an empty list / '' is CLEARED —
 * which means "carry the replaced row's", not "none": World's rule cannot say
 * none (no tag of a prefix = keep the old ones).
 */
export function withPickerFacets(choices: PackageChoices, patch: { readonly [K in PickerFacetKey]?: PickerFacets[K] | readonly string[] | '' }): PackageChoices {
  const next: PickerFacets = { ...(choices.pickerFacets ?? {}) };
  for (const key of Object.keys(patch) as PickerFacetKey[]) {
    const raw = patch[key];
    if (key === 'presentation' || key === 'family') {
      const list = [...new Set(((raw ?? []) as readonly string[]).map(facetValue).filter((v) => v !== ''))];
      if (list.length === 0) delete next[key];
      else next[key] = list;
    } else {
      const v = typeof raw === 'string' ? facetValue(raw) : '';
      if (v === '') delete next[key];
      else next[key] = v;
    }
  }
  const out = { ...choices };
  if (Object.keys(next).length === 0) delete out.pickerFacets;
  else out.pickerFacets = next;
  return out;
}

export function withFacets(choices: PackageChoices, facets: Partial<PackageFacets>): PackageChoices {
  return { ...choices, facets: { ...choices.facets, ...facets } };
}

export function withHeight(choices: PackageChoices, metres: number): PackageChoices {
  return { ...choices, heightMeters: { value: metres, provenance: 'declared' } };
}

/** Toggle a role; the last role cannot be removed (a package is FOR something). Dropping npc drops its block. */
export function withRole(choices: PackageChoices, role: PackageRole, on: boolean): PackageChoices {
  const has = choices.roles.includes(role);
  if (on === has) return choices;
  if (!on && choices.roles.length === 1) return choices;
  const roles = on ? [...choices.roles, role] : choices.roles.filter((r) => r !== role);
  const next: PackageChoices = { ...choices, roles };
  if (!on && role === 'npc') delete next.npc;
  return next;
}

export function withNpc(choices: PackageChoices, npc: { archetype: string; note?: string } | null): PackageChoices {
  const next = { ...choices };
  if (npc === null || npc.archetype.trim() === '') delete next.npc;
  else next.npc = { archetype: npc.archetype.trim(), ...(npc.note !== undefined && npc.note.trim() !== '' ? { note: npc.note.trim() } : {}) };
  return next;
}

/**
 * A new take joins the list; the FIRST take is starred by itself (one card is
 * the common case), every later one by a click — including after the starred
 * take was lifted with others remaining: an unreviewed frame is never the
 * card by default (Codex P2, #154 round two).
 */
export function withHeroTake(choices: PackageChoices, name: string): PackageChoices {
  if (choices.heroTakes.includes(name)) return choices;
  const next: PackageChoices = { ...choices, heroTakes: [...choices.heroTakes, name] };
  if (choices.heroTakes.length === 0) next.heroFavourite = name;
  return next;
}

/** Lift a take; the star moves off it (to nothing — the author chooses again). */
export function withoutHeroTake(choices: PackageChoices, name: string): PackageChoices {
  if (!choices.heroTakes.includes(name)) return choices;
  const next: PackageChoices = { ...choices, heroTakes: choices.heroTakes.filter((t) => t !== name) };
  if (choices.heroFavourite === name) delete next.heroFavourite;
  return next;
}

/** Star a take (must be one of the takes — the schema refuses otherwise), or clear the star. */
export function withHeroFavourite(choices: PackageChoices, name: string | undefined): PackageChoices {
  if (name === undefined) {
    if (choices.heroFavourite === undefined) return choices;
    const next = { ...choices };
    delete next.heroFavourite;
    return next;
  }
  if (!choices.heroTakes.includes(name)) throw new Error(`"${name}" is not one of the takes`);
  return { ...choices, heroFavourite: name };
}

export function withSelectionClip(choices: PackageChoices, clipId: string | undefined): PackageChoices {
  const next = { ...choices };
  if (clipId === undefined) delete next.selectionClip;
  else next.selectionClip = clipId;
  return next;
}

/** Pin one clip for (key, state) — replaces an existing pin for the same tuple. */
export function withPin(choices: PackageChoices, key: string, state: string, clipId: string): PackageChoices {
  const pin: PackagePin = { key, ...(state !== 'neutral' ? { state } : {}), clips: [clipId] };
  const pins = choices.pins.filter((p) => !(p.key === key && (p.state ?? 'neutral') === state));
  return { ...choices, pins: [...pins, pin] };
}

export function withoutPin(choices: PackageChoices, key: string, state: string): PackageChoices {
  const pins = choices.pins.filter((p) => !(p.key === key && (p.state ?? 'neutral') === state));
  return pins.length === choices.pins.length ? choices : { ...choices, pins };
}
